import { MessageCircle } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { waLink } from "@/lib/site";
import { useFeedback } from "@/components/Feedback";

const COLLECTIONS = [
  {
    name: "Rajwada Bridal",
    tag: "22KT Heirloom Sets",
    desc: "Layered haars, temple chokers and the complete bridal trousseau.",
    img: "/assets/bridal-bride.png",
    span: "md:col-span-7 md:row-span-2",
    testid: "collection-card-bridal",
  },
  {
    name: "Temple Nakshi",
    tag: "Antiqued Divinity",
    desc: "Hand-carved nakshi pendants blessed with pearl and ruby.",
    img: "/assets/temple-pendant.png",
    span: "md:col-span-5",
    testid: "collection-card-temple",
  },
  {
    name: "Aria Solitaires",
    tag: "IGI Certified",
    desc: "Brilliant-cut solitaires in whisper-light settings.",
    img: "/assets/solitaire.png",
    span: "md:col-span-5",
    testid: "collection-card-solitaire",
  },
  {
    name: "Bangle Atelier",
    tag: "Hand-Carved 22K",
    desc: "Patla, kada and daily-wear bangles with micro-carving.",
    img: "/assets/bangles.png",
    span: "md:col-span-4",
    testid: "collection-card-bangles",
  },
  {
    name: "Heritage Polki",
    tag: "Uncut Diamonds",
    desc: "Regal polki jhumkas with emerald drops.",
    img: "/assets/polki-earrings.png",
    span: "md:col-span-4",
    testid: "collection-card-polki",
  },
  {
    name: "Lakshmi Coins",
    tag: "24K · 999 Auspicious",
    desc: "Investment-grade coins for every shubh occasion.",
    img: "/assets/gold-coins.png",
    span: "md:col-span-4",
    testid: "collection-card-coins",
  },
];

export const Collections = () => {
  const { openEnquiry } = useFeedback();
  return (
    <section id="collections" data-testid="collections-section" className="relative mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-36">
      <SectionHead
        eyebrow="The Vault"
        title={<>Masterpieces, <span className="gold-text italic">hand-consecrated.</span></>}
        sub="Six signature collections, each piece weighed, hallmarked and itemised before your eyes."
      />
      <div className="grid auto-rows-[300px] grid-cols-1 gap-4 md:grid-cols-12">
        {COLLECTIONS.map((c, i) => (
          <Reveal key={c.name} delay={i * 0.06} className={c.span}>
            <div
              data-testid={c.testid}
              className="group relative h-full w-full overflow-hidden rounded-2xl border border-white/5 bg-[#101B36] transition-all duration-500 hover:border-[#D4AF37]/50 hover:shadow-[0_0_60px_rgba(212,175,55,0.12)]"
            >
              <img
                src={c.img}
                alt={c.name}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#060B18] via-[#060B18]/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#C59B27]">{c.tag}</p>
                <h3 className="mt-2 font-serif text-2xl text-white sm:text-3xl">{c.name}</h3>
                <p className="mt-1 max-w-sm text-xs font-light text-[#B5B0A8] sm:text-sm">{c.desc}</p>
                <div className="mt-4 flex translate-y-2 gap-3 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <button
                    data-testid={`${c.testid}-enquire-button`}
                    onClick={() => openEnquiry(`${c.name} Collection`, "collection")}
                    className="rounded-full bg-[#D4AF37] px-5 py-2 text-xs font-semibold text-black transition-transform hover:scale-105"
                  >
                    Request Details
                  </button>
                  <a
                    data-testid={`${c.testid}-whatsapp-button`}
                    href={waLink(`Namaste Aradhana Jewellers! I'd love to know more about the ${c.name} collection.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 rounded-full border border-[#D4AF37]/40 px-4 py-2 text-xs text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
                  >
                    <MessageCircle size={13} /> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
};
